import { themeEvents } from "../core/events";

const RUNTIME_KEY = "__rebalanceBoostPfsRuntime";

interface BoostPfsRuntime {
  cleanup: () => void;
}

interface BoostPfsRuntimeWindow extends Window {
  [RUNTIME_KEY]?: BoostPfsRuntime;
}

function callIfFunction(candidate: unknown): boolean {
  if (typeof candidate !== "function") {
    return false;
  }

  try {
    (candidate as () => void)();
    return true;
  } catch {
    return false;
  }
}

function refreshBoostPfs(reason: string): boolean {
  const api = window.BoostPFS ?? window.boostPFS;
  const invoked =
    callIfFunction(api?.init) ||
    callIfFunction(api?.refresh) ||
    callIfFunction(api?.refreshFilterTree) ||
    callIfFunction(api?.initFilterTree) ||
    callIfFunction(window.boostPFSFilter?.init) ||
    callIfFunction(window.boostPFSInstantSearch?.init);

  document.documentElement.setAttribute("data-build-boost-pfs-adapter", "ready");
  document.documentElement.setAttribute(
    "data-build-boost-pfs-last-refresh",
    `${reason}:${invoked ? "invoked" : "no-op"}`
  );
  window.dispatchEvent(
    new CustomEvent("rebalance:boost-pfs:refresh", {
      detail: { reason, invoked }
    })
  );

  return invoked;
}

export function initBoostPfs(): void {
  const runtimeWindow = window as BoostPfsRuntimeWindow;
  if (runtimeWindow[RUNTIME_KEY]) {
    return;
  }

  let refreshTimer = 0;
  const scheduleRefresh = (reason: string, delayMs = 180): void => {
    if (refreshTimer) {
      window.clearTimeout(refreshTimer);
    }

    refreshTimer = window.setTimeout(() => {
      refreshTimer = 0;
      refreshBoostPfs(reason);
    }, delayMs);
  };

  const handleGridUpdated = (): void => scheduleRefresh("collection-grid-updated");
  const unlistenRouteInitialized = themeEvents.on("route:initialized", ({ template }) => {
    if (template === "collection" || template === "search") {
      scheduleRefresh(`route-initialized:${template}`, 0);
    }
  });

  document.addEventListener("baseline:collection:grid-updated", handleGridUpdated);

  runtimeWindow[RUNTIME_KEY] = {
    cleanup: () => {
      document.removeEventListener("baseline:collection:grid-updated", handleGridUpdated);
      unlistenRouteInitialized();
      if (refreshTimer) {
        window.clearTimeout(refreshTimer);
        refreshTimer = 0;
      }
    }
  };

  scheduleRefresh("adapter-init", 0);
}
